import { createSearchClient } from 'lib/util/algolia';
import type { AlgoliaSearchHelper } from 'lib/util/algolia';
import { InstantSearch, useInstantSearch } from 'react-instantsearch-hooks-web';
import { RouterProps } from 'instantsearch.js/es/middlewares';
import AlgoliaConfigure from './AlgoliaConfigure';
import { AlgoliaSearchProps, RouteQueryParameters, RouteState } from './SearchTypes';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { googleAnalyticsMiddleware } from './AlgoliaGaMiddleware';
import { env } from 'lib/env';
import { useNmdpAppContext } from 'components/globalContext/NmdpAppProvider';
import { routerPropsFactory } from './routeMappers';
import { useDefaultSortByValue, useTcDirectoryRoutingFlag } from './useSortBySuffix';

/**
 * Note we switched from a useLayoutEffect to a useEffect here on 4/7/25; if we
 * start seeing crazy from GA middlewre, that's a likely culprint.
 */
const GaMiddleware = () => {
  const { use } = useInstantSearch();
  useEffect(() => {
    return use(googleAnalyticsMiddleware);
  });
  return <></>;
};

// If we are running server-side then use a fake search index - we don't want the results
const searchClient = createSearchClient(typeof window === 'undefined');

/**
 * The `AlgoliaSearch` component integrates Algolia's search functionality into the application.
 * It provides routing, state mapping, and search client configuration for Algolia's InstantSearch.
 *
 * @param {AlgoliaSearchProps} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child components to render within the search context.
 * @param {boolean} props.ifRouting - Determines whether routing is enabled for the search.
 * @param {boolean} props.routeIncludesSort - Indicates if the route should include sorting information.
 * @param {string} props.searchUrl - The URL of the search page for handling navigation.
 * @param {RouteQueryParameters} props.query - The query parameters from the route.
 *
 * @returns {JSX.Element} The rendered Algolia search component.
 *
 * @remarks
 * - The component uses `useSitecoreContext` to determine the current page state and context.
 * - It sets up a search helper using `useNmdpAppContext` to manage the Algolia search client.
 * - The `onSearchCallback` function ensures the search helper is updated and triggers a search.
 * - The `stateMapping` object maps the UI state to route parameters and vice versa.
 * - The `searchRouting` object configures Algolia's routing with a custom router and state mapping.
 * - The component handles compatibility issues between Algolia's router and Next.js router.
 *
 * @example
 * ```tsx
 * <AlgoliaSearch
 *   ifRouting={true}
 *   routeIncludesSort={true}
 *   searchUrl="/search"
 *   query={{ query: "example" }}
 * >
 *   <SearchResults />
 * </AlgoliaSearch>
 * ```
 */
const AlgoliaSearch = ({
  children,
  ifRouting,
  routeIncludesSort,
  searchUrl,
}: AlgoliaSearchProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  // Set app-wide search client.  Note we may need to useEffect this, in which case we should probably move the creation up to the app context
  const { setSearchHelper } = useNmdpAppContext();
  const onSearchCallback = useCallback(
    (helper: AlgoliaSearchHelper) => {
      const thisHelper = helper;
      setSearchHelper((current) => {
        // Is this the first time we're setting the helper??
        // If so, set it and return it to trigger re-render.
        if (!current) {
          return thisHelper;
        }
        // Do we have a reference to an old helper?
        // If so, discard it and re-render.
        if (!thisHelper) {
          return thisHelper;
        }
        // Otherwise, compare our current client to thisHelper's client, and as long
        // as they are the same, remain as-is.
        const isDirty = current.getClient() !== thisHelper.getClient();
        return isDirty ? thisHelper : current;
      });
      return thisHelper.search();
    },
    [setSearchHelper]
  );
  const searchIndex = env.ALGOLIA_INDEX_NAME;
  const addSortInfoToRoute = routeIncludesSort ?? true;
  const isSitecore = sitecoreContext.pageState !== 'normal';

  // Fix issue with Algolia router and Next router not playing nice togather
  const nextRouter = useRouter();

  useEffect(() => {
    // Algolia routing doesn't play nice with Next router,
    // So if we navigated back to search page, have next reload
    // the page so it gets rendered correctly
    const handleRouteChange = () => {
      if (window.location.pathname === searchUrl || sitecoreContext.hasSearch) {
        nextRouter.reload();
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
    // But why do we need to disable them?  See if we can't tighten this up...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultSortByValue = useDefaultSortByValue();
  const useTcDirectoryRouteState = useTcDirectoryRoutingFlag();
  const searchContextKey = `${sitecoreContext.itemPath ?? ''}::${defaultSortByValue ?? ''}::${
    useTcDirectoryRouteState ? 'tc' : 'default'
  }`;

  const searchRouting = useMemo<RouterProps<RouteState, RouteQueryParameters>>(() => {
    return routerPropsFactory({
      addSortInfoToRoute,
      defaultSortByValue,
      useTcDirectoryRouteState,
    });
  }, [addSortInfoToRoute, defaultSortByValue, useTcDirectoryRouteState]);

  const initialUiState = useMemo(() => {
    if (isSitecore || typeof window === 'undefined') {
      return undefined;
    }

    if (ifRouting) {
      const routeState = searchRouting.router?.read();
      const routedState = routeState
        ? searchRouting.stateMapping?.routeToState(routeState)
        : undefined;
      if (routedState) {
        return routedState;
      }
    }

    if (!defaultSortByValue) {
      return undefined;
    }

    return {
      [searchIndex]: {
        sortBy: defaultSortByValue,
      },
    };
  }, [defaultSortByValue, ifRouting, isSitecore, searchIndex, searchRouting]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const routingProp = ifRouting && !isSitecore ? (searchRouting as any) : undefined;
  // NOTE: I'm seeing full HTML without passing in uiState?  Not sure why but I'm not going to argue
  // const uiState = searchRouting.stateMapping?.routeToState(searchRouting.router?.read() ?? {});
  return (
    <InstantSearch
      key={searchContextKey}
      searchClient={searchClient}
      searchFunction={onSearchCallback}
      indexName={searchIndex}
      initialUiState={initialUiState}
      routing={routingProp}
    >
      <AlgoliaConfigure />
      {/*
      In theory, the fact that we set up the middleware with a useEffect should mean that we don't need
      this typeof window check, which should do wonders for our "server and client document mismatch"
      problems...but when we do the search box looses focus on keypress
      */}
      {typeof window !== 'undefined' && <GaMiddleware />}
      {children}
    </InstantSearch>
  );
};

export default AlgoliaSearch;
